import { useState } from "react";
import { DownloadSimple as Download, ShareNetwork as Share2 } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const C = { bg: "#FAF7F0", ink: "#2A1E5C", ink2: "#5D5A6B", muted: "#8B7E6A", gold: "#F5CB5C", goldDeep: "#C9A227", teal: "#2E86AB", line: "#E8E2D5" };
const longDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

let _logoImg = null;
const loadLogo = () => new Promise((resolve) => {
  if (_logoImg) return resolve(_logoImg);
  const img = new Image();
  img.onload = () => { _logoImg = img; resolve(img); };
  img.onerror = () => resolve(null);
  img.src = "/images/paydayrelaylogo.png";
});

async function drawScorecard(name, season) {
  const logo = await loadLogo();
  const W = 1650, H = 1275;
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  const x = cv.getContext("2d");

  x.fillStyle = "#FFFDF7"; x.fillRect(0, 0, W, H);
  x.strokeStyle = C.goldDeep; x.lineWidth = 10;
  x.strokeRect(32, 32, W - 64, H - 64);
  x.strokeStyle = C.ink; x.lineWidth = 2;
  x.strokeRect(56, 56, W - 112, H - 112);

  x.textAlign = "center";

  // Official logo (cropped to the wordmark + batons)
  if (logo) {
    x.drawImage(logo, 20, 130, 460, 210, W / 2 - 205, 82, 410, 187);
  } else {
    x.fillStyle = C.muted; x.font = "bold 36px Arial";
    x.fillText("P A Y D A Y   R E L A Y ", W / 2, 180);
  }

  x.fillStyle = C.ink; x.font = "bold 76px Georgia";
  x.fillText("YEAR-END SCORECARD", W / 2, 372);
  x.strokeStyle = C.goldDeep; x.lineWidth = 3;
  x.beginPath(); x.moveTo(W / 2 - 300, 402); x.lineTo(W / 2 + 300, 402); x.stroke();

  x.fillStyle = C.ink2; x.font = "italic 32px Georgia";
  x.fillText("This race was run and finished by", W / 2, 470);

  x.fillStyle = C.ink; x.font = "italic bold 88px Georgia";
  x.fillText(name || "A Dedicated Racer", W / 2, 580);
  x.strokeStyle = C.gold; x.lineWidth = 4;
  x.beginPath(); x.moveTo(W / 2 - 380, 614); x.lineTo(W / 2 + 380, 614); x.stroke();

  x.fillStyle = C.ink2; x.font = "36px Georgia";
  x.fillText("completed the 52-week Payday Relay race", W / 2, 688);

  if (season.season_start) {
    x.fillStyle = C.muted; x.font = "30px Arial";
    const end = season.season_end || season.today;
    x.fillText(`${longDate(season.season_start)}, ${longDate(end)}`, W / 2, 740);
  }

  x.fillStyle = C.ink; x.font = "bold 44px Georgia";
  x.fillText(`${season.cleared} of ${season.weeks_total} weekly targets cleared`, W / 2, 832);
  x.fillStyle = C.ink2; x.font = "30px Arial";
  x.fillText("A full year of showing up, rhythm, not luck.", W / 2, 884);

  // Rest built into the race (time off tallies)
  const t = season.time_off || {};
  const rest = [];
  if (t.pto_days) rest.push(`${t.pto_days} PTO day${t.pto_days === 1 ? "" : "s"}`);
  if (t.pto_weeks) rest.push(`${t.pto_weeks} PTO week${t.pto_weeks === 1 ? "" : "s"}`);
  if (t.sick_days) rest.push(`${t.sick_days} sick day${t.sick_days === 1 ? "" : "s"}`);
  if (t.days_off) rest.push(`${t.days_off} day${t.days_off === 1 ? "" : "s"} off`);
  if (rest.length) {
    let line = rest.join("  ·  ");
    if (t.covered_total > 0) {
      line += `, $${Number(t.covered_total).toLocaleString("en-US", { maximumFractionDigits: 0 })} of rest, self-paid`;
    }
    x.fillStyle = C.teal; x.font = "28px Arial";
    x.fillText(line, W / 2, 938);
  }

  // Gold seal
  x.strokeStyle = C.goldDeep; x.lineWidth = 6;
  x.beginPath(); x.arc(340, 1030, 82, 0, Math.PI * 2); x.stroke();
  x.strokeStyle = C.goldDeep; x.lineWidth = 1.5;
  x.beginPath(); x.arc(340, 1030, 70, 0, Math.PI * 2); x.stroke();
  x.fillStyle = C.ink; x.font = "bold 52px Georgia";
  x.fillText("52", 340, 1032);
  x.fillStyle = C.muted; x.font = "bold 19px Arial";
  x.fillText("R E L A Y S", 340, 1066);

  // Signature
  x.fillStyle = C.ink; x.font = "italic 42px Georgia";
  x.fillText("Shari Dove Shepard", W - 380, 1030);
  x.strokeStyle = C.ink; x.lineWidth = 2;
  x.beginPath(); x.moveTo(W - 560, 1052); x.lineTo(W - 200, 1052); x.stroke();
  x.fillStyle = C.muted; x.font = "26px Arial";
  x.fillText("Founder", W - 380, 1092);

  x.fillStyle = C.muted; x.font = "26px Arial";
  x.fillText("paydayrelay.com", W / 2, 1168);

  return cv;
}

export const FinisherCertificate = () => {
  const [name, setName] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [season, setSeason] = useState(null);

  const create = async () => {
    if (busy) return;
    setBusy(true);
    try {
      let d = season;
      if (!d) {
        const res = await fetch(`${API}/api/caddy/season`, { credentials: "include" });
        d = await res.json();
        if (!res.ok) { toast.error(d.detail || "Couldn't load your race"); return; }
        setSeason(d);
      }
      const cv = await drawScorecard(name.trim(), d);
      setPreview(cv.toDataURL("image/png"));
    } catch {
      toast.error("Network hiccup, try again.");
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    if (!season) return;
    try {
      const cv = await drawScorecard(name.trim(), season);
      const a = document.createElement("a");
      a.href = cv.toDataURL("image/png");
      a.download = "payday-relay-year-end-scorecard.png";
      a.click();
      toast.success("Saved to your device 🏆");
    } catch {
      toast.error("Couldn't save, try again");
    }
  };

  const share = async () => {
    if (!season) return;
    try {
      const cv = await drawScorecard(name.trim(), season);
      const blob = await new Promise((res) => cv.toBlob(res, "image/png"));
      if (!blob) throw new Error("no-blob");
      const file = new File([blob], "payday-relay-year-end-scorecard.png", { type: "image/png" });
      const payload = {
        title: "My Payday Relay Year-End Scorecard",
        text: "52 relays. A full year of showing up for my own race. 🏁",
      };
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ ...payload, files: [file] });
        return;
      }
      if (navigator.share) {
        await navigator.share({ ...payload, url: "https://paydayrelay.com" });
        return;
      }
      // Fallback: copy caption, offer download
      try { await navigator.clipboard.writeText(`${payload.title}, ${payload.text} paydayrelay.com`); } catch (_ignore) { /* clipboard may be unavailable */ }
      toast.success("Caption copied, save the image and paste it into your post");
      download();
    } catch (e) {
      if (e && e.name === "AbortError") return;
      toast.error("Couldn't open share, try Download instead");
    }
  };

  return (
    <div className="rounded-2xl p-5 border-2" style={{ background: "#FFFDF5", borderColor: C.gold }} data-testid="caddy-finisher-card">
      <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: "#7A5E10" }}>🏆 Race complete</p>
      <p className="text-sm font-bold mb-1.5" style={{ color: C.ink }}>You crossed the finish line, all 52 relays</p>
      <p className="text-xs leading-relaxed mb-3" style={{ color: C.ink2 }}>
        Add your name and keep this however you like, save it, share it, tuck it into a folder for a day you need the reminder. A full year of showing up for your own race.
      </p>
      <input
        type="text"
        value={name}
        onChange={(e) => { setName(e.target.value); setPreview(null); }}
        placeholder="Your name, as you want it on the scorecard"
        maxLength={40}
        className="w-full px-4 py-3 rounded-xl border text-sm mb-2"
        style={{ borderColor: C.line, color: C.ink, background: "#FFFFFF" }}
        data-testid="finisher-name-input"
      />
      {preview && (
        <img src={preview} alt="Year-End Scorecard preview" className="w-full rounded-xl border mb-2" style={{ borderColor: C.line }} data-testid="finisher-preview" />
      )}
      {!preview ? (
        <button
          type="button"
          onClick={create}
          disabled={busy}
          className="w-full py-3 rounded-full font-bold text-sm disabled:opacity-40 transition-transform hover:scale-[1.02]"
          style={{ background: C.gold, color: C.ink }}
          data-testid="finisher-create-btn"
        >
          {busy ? "Preparing your scorecard…" : "Create my Year-End Scorecard"}
        </button>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={download}
            className="py-3 rounded-full font-bold text-sm inline-flex items-center justify-center gap-1.5 transition-transform hover:scale-[1.02]"
            style={{ background: C.ink, color: "#fff" }}
            data-testid="finisher-download-btn"
          >
            <Download className="w-4 h-4" /> Download
          </button>
          <button
            type="button"
            onClick={share}
            className="py-3 rounded-full font-bold text-sm inline-flex items-center justify-center gap-1.5 transition-transform hover:scale-[1.02]"
            style={{ background: C.gold, color: C.ink }}
            data-testid="finisher-share-btn"
          >
            <Share2 className="w-4 h-4" /> Share
          </button>
        </div>
      )}
    </div>
  );
};
